// frontend/src/components/DeduplicationCampaignManager.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel,
  Container,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SubPageHeader from './SubPageHeader';
import CampaignSelector from './dedup/CampaignSelector';
import SourceManager from './dedup/SourceManager';
import DuplicateRuleConfig from './dedup/DuplicateRuleConfig';
import DeduplicationProgress from './dedup/DeduplicationProgress';
import FinalListPreview from './dedup/FinalListPreview';

const steps = [
  'Kampagne auswählen',
  'Quellen priorisieren',
  'Duplikats-Regeln',
  'Deduplizierung',
  'Finale Liste'
];

interface Campaign {
  id: number;
  name: string;
  description?: string;
}

interface Source {
  id: number;
  source_type: 'external' | 'internal';
  source_id: number | null;
  priority: number;
  description: string;
  record_count: number;
  status: string;
}

interface DedupRule {
  match_fields: string[];
  match_threshold: number;
}

export default function DeduplicationCampaignManager() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [dedupRule, setDedupRule] = useState<DedupRule>({
    match_fields: ['telefon', 'email'],
    match_threshold: 90
  });
  const [dedupStatus, setDedupStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    if (activeStep === 0 && !selectedCampaign) {
      setError('Bitte wählen Sie eine Kampagne aus');
      return;
    }
    if (activeStep === 1 && sources.length === 0) {
      setError('Bitte fügen Sie mindestens eine Quelle hinzu');
      return;
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedCampaign(null);
    setSources([]);
    setDedupRule({ match_fields: ['telefon', 'email'], match_threshold: 90 });
    setDedupStatus('idle');
    setError(null);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <CampaignSelector
            selectedCampaign={selectedCampaign}
            onSelect={setSelectedCampaign}
          />
        );
      case 1:
        return (
          <SourceManager
            campaignId={selectedCampaign?.id || 0}
            sources={sources}
            onSourcesChange={setSources}
          />
        );
      case 2:
        return (
          <DuplicateRuleConfig
            campaignId={selectedCampaign?.id || 0}
            rule={dedupRule}
            onRuleChange={setDedupRule}
          />
        );
      case 3:
        return (
          <DeduplicationProgress
            campaignId={selectedCampaign?.id || 0}
            sources={sources}
            rule={dedupRule}
            status={dedupStatus}
            onStatusChange={setDedupStatus}
          />
        );
      case 4:
        return (
          <FinalListPreview
            campaignId={selectedCampaign?.id || 0}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <SubPageHeader title="Deduplizierung" />
      
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: isMobile ? 2 : 4,
          px: isMobile ? 1 : 3
        }}
      >
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'}
            gutterBottom 
            sx={{ 
              color: '#003366',
              fontWeight: 600,
              fontSize: isMobile ? '1.25rem' : '1.75rem'
            }}
          >
            Deduplizierung & Priorisierung
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#6b7280',
              fontSize: isMobile ? '0.9rem' : '0.95rem'
            }}
          >
            Erstellen Sie priorisierte, deduplizierte Listen aus mehreren Datenquellen
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Card 
          sx={{ 
            mb: isMobile ? 2 : 4,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb'
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Stepper 
              activeStep={activeStep} 
              orientation={isSmallMobile ? 'vertical' : 'horizontal'}
              sx={{ mb: isMobile ? 3 : 4 }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: isMobile ? '0.85rem' : '0.95rem',
                        mt: isSmallMobile ? 0.5 : 0
                      }
                    }}
                  >
                    {isSmallMobile ? label : (isMobile && label.length > 15 ? `${label.substring(0, 12)}...` : label)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ minHeight: isMobile ? 300 : 400 }}>
              {renderStepContent(activeStep)}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              mt: isMobile ? 3 : 4,
              gap: isMobile ? 1 : 0
            }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                fullWidth={isMobile}
                sx={{
                  color: '#003366',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 51, 102, 0.04)'
                  },
                  order: isMobile ? 2 : 1
                }}
              >
                Zurück
              </Button>
              <Box sx={{ order: isMobile ? 1 : 2 }}>
                {activeStep === steps.length - 1 ? (
                  <Button 
                    variant="contained"
                    onClick={handleReset}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: '#003366',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      '&:hover': {
                        backgroundColor: '#002147'
                      }
                    }}
                  >
                    Neue Kampagne
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={dedupStatus === 'running'}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: '#003366',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      '&:hover': {
                        backgroundColor: '#002147'
                      }
                    }}
                  >
                    {activeStep === 3 ? 'Zur finalen Liste' : 'Weiter'}
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
